import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, X, Send, Loader2, CheckCircle2, ShieldCheck, EyeOff, Handshake, Sparkles, ArrowRight, Mic, Square } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PlatformLayout } from "./PlatformLayout";
import { submitProperty, uploadSubmissionFile, API, reformulateMessage } from "./api";
import { GoldWave } from "./EspaceAcquereur";

const TYPES = ["Appartement", "Maison", "Terrain", "Immeuble", "Autre"];

const EMPTY = {
  owner_nom: "", owner_prenom: "", owner_telephone: "", owner_email: "",
  type: "Maison", price: "", address: "", city: "", postal_code: "",
  surface: "", rooms: "", bedrooms: "", description: "",
  images: [], consent: false,
};

const ARGS = [
  { icon: EyeOff, t: "Vente confidentielle", d: "Pas de panneau, pas de portails : votre bien reste discret, présenté à un réseau ciblé d'acquéreurs qualifiés." },
  { icon: ShieldCheck, t: "Estimation offerte", d: "Vincent Marette étudie votre bien et vous propose une estimation juste, sans engagement." },
  { icon: Handshake, t: "Accompagnement dédié", d: "Un conseiller vous est affecté et vous accompagne du premier contact jusqu'à la signature." },
];

export default function ProprietaireSpace() {
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const photoRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recap, setRecap] = useState("");
  const [recapLoading, setRecapLoading] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const buildRecap = async (text) => {
    if (!text || text.trim().length < 12) return;
    setRecapLoading(true);
    try { const d = await reformulateMessage(text); if (d && d.text) setRecap(d.text.trim()); }
    catch (_) {}
    setRecapLoading(false);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", new Blob(chunksRef.current, { type: "audio/webm" }), "bien.webm");
          const res = await fetch(`${API}/ai/transcribe`, { method: "POST", body: fd });
          const d = await res.json();
          const spoken = (d.text || "").trim();
          if (spoken) {
            const full = (f.description ? f.description + " " : "") + spoken;
            setF((s) => ({ ...s, description: (s.description ? s.description + " " : "") + spoken }));
            buildRecap(full);
          }
        } catch (_) {}
        setTranscribing(false);
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch (_) {}
  };
  const stopRec = () => { if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); } };

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const inp = "w-full bg-white border border-zinc-300 rounded-[5px] px-4 py-3 text-sm text-[#050505] focus:outline-none focus:border-[#C19A6B] transition-colors";
  const lbl = "block text-xs uppercase tracking-wider text-zinc-500 mb-1.5";

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) { const r = await uploadSubmissionFile(file); urls.push(r.url); }
      set("images", [...f.images, ...urls]);
    } catch (_) { setErr("Un envoi de photo a échoué. Réessayez."); }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.owner_nom.trim() || !f.owner_telephone.trim()) { setErr("Merci d'indiquer votre nom et votre téléphone."); return; }
    if (!f.consent) { setErr("Merci d'accepter la politique de confidentialité (RGPD)."); return; }
    setSending(true);
    try {
      await submitProperty({
        ...f,
        price: f.price ? Number(f.price) : 0,
        surface: f.surface ? Number(f.surface) : 0,
        rooms: f.rooms ? Number(f.rooms) : 0,
        bedrooms: f.bedrooms ? Number(f.bedrooms) : 0,
        source: "espace_proprietaire", origin: "espace_proprietaire",
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) { setErr(e2.message || "Envoi impossible."); }
    setSending(false);
  };

  if (done) {
    return (
      <PlatformLayout>
        <section className="max-w-2xl mx-auto px-6 py-32 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <CheckCircle2 className="w-16 h-16 mx-auto text-[#C19A6B] mb-6" />
            <h1 className="text-3xl sm:text-4xl font-light text-[#050505]">Votre bien a bien été transmis</h1>
            <p className="mt-4 text-zinc-600 leading-relaxed">Merci ! Vincent Marette étudie personnellement votre bien et vous recontacte très rapidement pour affecter le conseiller le plus adapté à votre projet.</p>
            <button onClick={() => nav("/")} data-testid="proprio-back-home" className="mt-8 inline-flex items-center gap-2 bg-[#050505] hover:bg-[#1a1a1a] text-white text-sm uppercase tracking-wider px-8 py-3.5 rounded-[5px] transition-colors">Retour à l'accueil <ArrowRight className="w-4 h-4" /></button>
          </motion.div>
        </section>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div data-testid="proprietaire-space" className="bg-[#F1F3F5]">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#080809] text-white py-24 sm:py-28">
          <div aria-hidden="true" className="absolute -top-24 -right-24 w-[30rem] h-[30rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "#C19A6B1f" }} />
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-[#C19A6B]">Espace Propriétaire</span>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1]">Vendez votre bien <br /><span className="font-semibold italic text-[#C19A6B]">en toute discrétion</span></h1>
            <p className="mt-5 text-base sm:text-lg font-light text-zinc-400 max-w-2xl mx-auto leading-relaxed">Particulier ? Confiez-nous votre bien : nous l'étudions, l'estimons gratuitement et l'affectons à un conseiller de confiance qui le présentera à notre réseau d'acquéreurs off market.</p>
          </div>
        </section>

        {/* Arguments */}
        <section className="max-w-6xl mx-auto px-6 -mt-12 relative z-20">
          <div className="grid sm:grid-cols-3 gap-5">
            {ARGS.map((a, i) => (
              <motion.div key={a.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="bg-white border border-zinc-200 rounded-[5px] p-6 shadow-lg shadow-black/5">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-[5px] bg-[#C19A6B]/10 text-[#C19A6B]"><a.icon className="w-5 h-5" /></span>
                <h3 className="mt-4 text-base font-medium text-[#050505]">{a.t}</h3>
                <p className="mt-2 text-sm font-light text-zinc-600 leading-relaxed">{a.d}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {err && <div className="max-w-3xl mx-auto px-6 mt-10"><div className="bg-red-500/10 border border-red-500/30 text-red-600 text-sm px-4 py-3 rounded-[5px]" data-testid="proprio-error">{err}</div></div>}

        <form onSubmit={submit} className="max-w-3xl mx-auto px-6 py-16 space-y-12" data-testid="proprietaire-form">
          {/* Vos coordonnées */}
          <div className="space-y-5">
            <h2 className="text-xl font-light text-[#050505] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C19A6B]" /> Vos coordonnées</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={lbl}>Nom *</label><input required value={f.owner_nom} onChange={(e) => set("owner_nom", e.target.value)} className={inp} data-testid="proprio-nom" /></div>
              <div><label className={lbl}>Prénom</label><input value={f.owner_prenom} onChange={(e) => set("owner_prenom", e.target.value)} className={inp} data-testid="proprio-prenom" /></div>
              <div><label className={lbl}>Téléphone *</label><input required value={f.owner_telephone} onChange={(e) => set("owner_telephone", e.target.value)} className={inp} data-testid="proprio-tel" /></div>
              <div><label className={lbl}>E-mail</label><input type="email" value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} className={inp} data-testid="proprio-email" /></div>
            </div>
          </div>

          {/* Votre bien */}
          <div className="space-y-5">
            <h2 className="text-xl font-light text-[#050505]">Votre bien</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={lbl}>Type de bien</label><select value={f.type} onChange={(e) => set("type", e.target.value)} className={inp} data-testid="proprio-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
              <div><label className={lbl}>Prix souhaité (€)</label><input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="proprio-prix" /></div>
              <div className="sm:col-span-2"><label className={lbl}>Adresse</label><input value={f.address} onChange={(e) => set("address", e.target.value)} className={inp} data-testid="proprio-adresse" /></div>
              <div><label className={lbl}>Ville</label><input value={f.city} onChange={(e) => set("city", e.target.value)} className={inp} data-testid="proprio-ville" /></div>
              <div><label className={lbl}>Code postal</label><input value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} className={inp} data-testid="proprio-cp" /></div>
              <div><label className={lbl}>Surface (m²)</label><input type="number" value={f.surface} onChange={(e) => set("surface", e.target.value)} className={inp} data-testid="proprio-surface" /></div>
              <div><label className={lbl}>Nombre de pièces</label><input type="number" value={f.rooms} onChange={(e) => set("rooms", e.target.value)} className={inp} data-testid="proprio-pieces" /></div>
            </div>
            <div>
              <label className={lbl}>Décrivez votre bien</label>
              {/* Dictée vocale premium — style visite IA, onde dorée */}
              <div className="relative overflow-hidden bg-white border border-zinc-200 rounded-[5px] p-5 mb-3 text-center" style={{ boxShadow: "0 12px 40px -20px rgba(17,24,39,0.2)" }} data-testid="proprio-voice">
                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#C19A6B]">Dictée vocale</p>
                <p className="text-sm text-zinc-600 mt-1">Décrivez votre bien à voix haute, notre IA rédige pour vous.</p>
                <div className="mt-3"><GoldWave active={recording} /></div>
                <div className="mt-3">
                  {!recording ? (
                    <motion.button type="button" onClick={startRec} disabled={transcribing} data-testid="proprio-mic-start"
                      animate={{ scale: [1, 1.06, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} whileHover={{ scale: 1.1 }}
                      className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-wider rounded-[5px] px-5 py-2.5 disabled:opacity-60"
                      style={{ background: "#C19A6B", boxShadow: "0 0 26px rgba(193,154,107,0.55)" }}>
                      {transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />} {transcribing ? "Transcription…" : "Recherche vocale"}
                    </motion.button>
                  ) : (
                    <button type="button" onClick={stopRec} data-testid="proprio-mic-stop"
                      className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-wider rounded-[5px] px-5 py-2.5"
                      style={{ background: "#C19A6B", boxShadow: "0 0 26px rgba(193,154,107,0.55)" }}>
                      <Square className="w-5 h-5" /> Terminer
                    </button>
                  )}
                </div>
              </div>
              {(recapLoading || recap) && (
                <div className="bg-[#FBF8F3] border border-[#C19A6B]/30 rounded-[5px] p-4 mb-3" data-testid="proprio-recap">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#A88358] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Résumé optimisé pour présenter votre bien</p>
                  {recapLoading ? (
                    <p className="text-sm text-zinc-500 mt-2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Rédaction en cours…</p>
                  ) : (
                    <>
                      <p className="text-sm text-zinc-700 mt-2 leading-relaxed whitespace-pre-line">{recap}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button type="button" onClick={() => { set("description", recap); setRecap(""); }} data-testid="proprio-recap-use" className="inline-flex items-center gap-1.5 text-white text-[11px] font-semibold uppercase tracking-wider rounded-[5px] px-3.5 py-1.5" style={{ background: "#C19A6B" }}><CheckCircle2 className="w-3.5 h-3.5" /> Utiliser ce texte</button>
                        <button type="button" onClick={() => setRecap("")} data-testid="proprio-recap-dismiss" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-800 text-[11px] font-semibold uppercase tracking-wider rounded-[5px] px-3 py-1.5 border border-zinc-200">Ignorer</button>
                      </div>
                    </>
                  )}
                </div>
              )}
              <textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} className={inp} placeholder="Atouts, environnement, travaux éventuels, disponibilité…" data-testid="proprio-desc" />
            </div>
          </div>

          {/* Photos */}
          <div className="space-y-3">
            <h2 className="text-xl font-light text-[#050505]">Photos (facultatif)</h2>
            <div className="flex flex-wrap gap-3">
              {f.images.map((src, i) => (
                <div key={i} className="relative w-28 h-20 group">
                  <img src={src} alt="" className="w-full h-full object-cover border border-zinc-200 rounded-[5px]" />
                  <button type="button" onClick={() => set("images", f.images.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" onClick={() => photoRef.current.click()} className="w-28 h-20 border border-dashed border-zinc-300 rounded-[5px] flex items-center justify-center text-zinc-500 hover:border-[#C19A6B]/60" data-testid="proprio-upload"><Upload className="w-5 h-5" /></button>
              <input ref={photoRef} type="file" accept="image/*" multiple onChange={uploadPhotos} className="hidden" />
            </div>
            {uploading && <p className="text-xs text-[#C19A6B] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Envoi en cours…</p>}
          </div>

          {/* RGPD + submit */}
          <div className="space-y-5 border-t border-zinc-200 pt-8">
            <label className="flex items-start gap-3 text-sm text-zinc-600 cursor-pointer">
              <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#C19A6B]" data-testid="proprio-consent" />
              <span>J'accepte que mes informations soient collectées et utilisées par OFF MARKETTE (Vincent Marette) pour l'étude de mon bien, conformément à la politique de confidentialité (RGPD).</span>
            </label>
            <button type="submit" disabled={sending || uploading} className="flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-8 py-4 rounded-[5px] transition-colors disabled:opacity-60" data-testid="proprio-submit">
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Send className="w-4 h-4" /> Confier mon bien</>}
            </button>
          </div>
        </form>
      </div>
    </PlatformLayout>
  );
}
